<template>
  <div v-if="hasRoundSizes" class="round-sizes-wrapper">
    <RoundSizesList
      :round-sizes="roundSizesLocal"
      :is-vs-competition="!!competition.is_vs"
      :can-control-stages="canControlStages"
      @toggle-stage="handleToggleStage"
    />
    <ConfirmationModal
      ref="confirmationModal"
      :message="confirmModalMessage"
      :showCancel="confirmShowCancel"
      @confirmed="confirmStageAction"
      @canceled="cancelStageAction"
    />
  </div>
</template>

<script>
import RoundSizesList from "@/components/competitions/RoundSizesList.vue";
import ConfirmationModal from "@/components/competitions/ConfirmationModal.vue";
import { isOwnerOrganizer } from "@/helpers/roleUtils";
import { mapState } from "vuex";

export default {
  name: "CompetitionRoundSizes",
  components: { RoundSizesList, ConfirmationModal },
  props: {
    competition: { type: Object, required: true }
  },
  data() {
    return {
      togglingStage: false,
      roundSizesLocal: [],
      confirmTargetStage: null,
      confirmTargetActive: false,
      confirmModalMessage: "",
      confirmShowCancel: true,
    };
  },
  computed: {
    ...mapState('user', ['token', 'user']),
    canControlStages() {
      return isOwnerOrganizer(this.user, this.competition);
    },
    hasRoundSizes() {
      const rounds = this.roundSizesLocal;
      return Array.isArray(rounds) && rounds.length > 0;
    }
  },
  watch: {
    'competition.round_sizes': {
      immediate: true,
      deep: true,
      handler(val) {
        if (Array.isArray(val)) {
          this.roundSizesLocal = [...val];
        }
      }
    }
  },
  methods: {
    orderedStagesAsc() {
      const list = this.roundSizesLocal || [];
      return [...list].sort((a, b) => (a?.id || 0) - (b?.id || 0));
    },
    async handleToggleStage(stage) {
      if (!this.canControlStages || !stage || !stage.id || this.togglingStage) return;
      if (!stage.active && !stage.activated_at) {
        console.warn('No puedes activar esta etapa hasta que se llene el cupo y el backend la marque como disponible.');
        return;
      }
      if (!stage.active && this.hasPriorInactiveStage(stage)) {
        this.confirmTargetStage = null;
        this.confirmTargetActive = false;
        this.confirmShowCancel = false;
        this.confirmModalMessage = this.$t?.('competitions.rounds.reactivate_blocked') || 'No puedes reactivar esta etapa hasta reactivar manualmente la etapa previa.';
        this.$refs.confirmationModal?.openModal();
        return;
      }
      if (stage.active && this.hasLaterActiveStage(stage)) {
        this.confirmTargetStage = null;
        this.confirmTargetActive = false;
        this.confirmShowCancel = false;
        this.confirmModalMessage = this.$t?.('competitions.rounds.deactivate_blocked') || 'No puedes desactivar una etapa intermedia porque hay una posterior activa.';
        this.$refs.confirmationModal?.openModal();
        return;
      }
      this.confirmTargetStage = stage;
      this.confirmTargetActive = !stage.active;
      this.confirmModalMessage = this.buildStageMessage(stage, this.confirmTargetActive);
      this.confirmShowCancel = true;
      this.$refs.confirmationModal?.openModal();
    },
    async performStageToggle(stage, targetActive) {
      if (!stage || !stage.id) return;
      this.togglingStage = true;
      try {
        await this.updateStageActive(stage.id, targetActive);
      } catch (err) {
        console.error('Error al actualizar etapa', err?.response?.data || err);
      } finally {
        this.togglingStage = false;
      }
    },
    confirmStageAction() {
      if (!this.confirmTargetStage) return;
      const stage = this.confirmTargetStage;
      const targetActive = this.confirmTargetActive;
      this.confirmTargetStage = null;
      this.confirmTargetActive = false;
      this.performStageToggle(stage, targetActive);
    },
    cancelStageAction() {
      this.confirmTargetStage = null;
      this.confirmTargetActive = false;
    },
    buildStageMessage(stage, targetActive) {
      const name = this.stageLabelSafe(stage);
      if (targetActive) {
        return this.$t?.('competitions.rounds.activate_confirm', { name }) || `Activar ${name}. Esta etapa se usará en la competencia.`;
      }
      return this.$t?.('competitions.rounds.deactivate_confirm_single', { name }) || `Desactivar ${name}. Esta etapa no se usará en la competencia.`;
    },
    stageLabelSafe(stage) {
      if (!stage) return '';
      const key = `rounds.stage${stage.id}`;
      return this.$te && this.$te(key) ? this.$t(key) : stage.name || `Stage ${stage.id}`;
    },
    hasLaterActiveStage(stage) {
      if (!stage || !Array.isArray(this.roundSizesLocal)) return false;
      return this.roundSizesLocal.some((s) => s.active && s.id > stage.id);
    },
    hasPriorInactiveStage(stage) {
      if (!stage || !Array.isArray(this.roundSizesLocal)) return false;
      return this.roundSizesLocal.some((s) => s.id < stage.id && !s.active && s.activated_at);
    },
    applyRoundSizePatch(roundSize) {
      if (!roundSize || !roundSize.id || !Array.isArray(this.roundSizesLocal)) return;
      const idx = this.roundSizesLocal.findIndex((r) => r.id === roundSize.id);
      if (idx === -1) return;
      const next = { ...this.roundSizesLocal[idx], ...roundSize };
      if (this.$set) {
        this.$set(this.roundSizesLocal, idx, next);
      } else {
        this.roundSizesLocal.splice(idx, 1, next);
      }
      this.syncRoundSizesToStore();
    },
    syncRoundSizesToStore() {
      const payload = Array.isArray(this.roundSizesLocal) ? [...this.roundSizesLocal] : [];
      this.$store.dispatch('competition/upsertCompetition', {
        round_sizes: payload,
        roundSizes: payload,
      });
    },
    async updateStageActive(stageId, active) {
      const competitionId = this.competition?.id;
      if (!competitionId || !stageId) return;
      const url = `/competitions/${competitionId}/round-sizes/${stageId}/lock`;
      const payload = { active };
      const headers = this.token ? { Authorization: `Bearer ${this.token}` } : {};
      const { data } = await this.$axios.post(url, payload, { headers });
      const updated = data?.round_size || data;
      if (updated) this.applyRoundSizePatch(updated);
    },
  }
};
</script>

<style scoped>
.round-sizes-wrapper {
  grid-column: 1 / -1;
  margin-top: 4px;
  overflow: visible;
}
</style>
